// src/pages/Dashboard.tsx
import { useQuery } from '@tanstack/react-query';
import { Briefcase, FileText, TrendingUp, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface Application {
  id: number;
  company: string;
  position: string;
  status: string;
  applied_date: string;
  interview_count: number;
}

function Dashboard() {
  const { data: applicationsData, isLoading, error } = useQuery<{ applications: Application[] }>({
    queryKey: ['applications'],
    queryFn: async () => {
      const response = await fetch('/api/applications');
      if (!response.ok) {
        throw new Error(`Failed to load applications: ${response.statusText}`);
      }
      return response.json();
    },
  });

  const applications: Application[] = applicationsData?.applications || [];
  
  const stats = {
    total: applications.length,
    applied: applications.filter(a => a.status === 'applied').length,
    interviewing: applications.filter(a => a.status === 'interviewing').length,
    offers: applications.filter(a => a.status === 'offer').length,
  };

  const recentApplications = applications.slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">Track your job search progress</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Applications"
          value={stats.total}
          icon={<Briefcase className="h-6 w-6" />}
          color="blue"
        />
        <StatCard
          title="Applied"
          value={stats.applied}
          icon={<FileText className="h-6 w-6" />}
          color="yellow"
        />
        <StatCard
          title="Interviewing"
          value={stats.interviewing}
          icon={<TrendingUp className="h-6 w-6" />}
          color="purple"
        />
        <StatCard
          title="Offers"
          value={stats.offers}
          icon={<Clock className="h-6 w-6" />}
          color="green"
        />
      </div>

      {/* Recent Applications */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Applications</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="px-6 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading applications...</p>
            </div>
          ) : error ? (
            <div className="px-6 py-8 text-center">
              <div className="text-red-600 mb-2">
                <Briefcase className="h-8 w-8 mx-auto" />
              </div>
              <p className="text-sm font-medium text-gray-900">Failed to load applications</p>
              <p className="mt-1 text-sm text-gray-500">{error instanceof Error ? error.message : 'An error occurred'}</p>
            </div>
          ) : recentApplications.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No applications yet. Start tracking your job search!
            </div>
          ) : (
            recentApplications.map(app => (
              <div key={app.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{app.position}</h3>
                    <p className="text-sm text-gray-600">{app.company}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <StatusBadge status={app.status} />
                    <span className="text-sm text-gray-500">
                      {app.applied_date && format(new Date(app.applied_date), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'yellow' | 'purple' | 'green';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusStyles: Record<string, string> = {
    applied: 'bg-blue-100 text-blue-800',
    screening: 'bg-yellow-100 text-yellow-800',
    interviewing: 'bg-purple-100 text-purple-800',
    offer: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.applied}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default Dashboard;
